/**
 * MasterDetailShell Component
 * 
 * A Jira-style split-panel layout with:
 * - Master list panel (resizable)
 * - Detail panel (shows when item selected)
 * - Responsive behavior (modal on mobile)
 * - URL state synchronization
 * - Keyboard navigation support
 */

import { type ReactNode, useEffect, useRef, useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { ResizableDivider } from './ResizableDivider';
import { useMasterDetailState, type MasterDetailStateOptions } from './useMasterDetailState';

// ============================================================================
// Types
// ============================================================================

export interface MasterDetailShellProps {
  /** Master panel content (usually a CollectionView) */
  master: ReactNode;
  
  /** Detail panel content (usually a CandidateViewShell) */
  detail: ReactNode | null;
  
  /** ID of the currently selected item */
  selectedId?: string | null;
  
  /** Callback when selection changes */
  onSelectionChange?: (id: string | null) => void;
  
  /** Default width of master panel in pixels */
  defaultMasterWidth?: number;
  
  /** Minimum width of master panel */
  minMasterWidth?: number;
  
  /** Maximum width of master panel */
  maxMasterWidth?: number;
  
  /** URL parameter name for selected item */
  urlParamName?: string;
  
  /** Whether to sync selection to URL */
  syncToUrl?: boolean;
  
  /** Storage key for persisting panel width */
  widthStorageKey?: string;
  
  /** Enable keyboard shortcuts (Escape to close) */
  enableKeyboardShortcuts?: boolean;
  
  /** Breakpoint for mobile behavior (default: 768) */
  mobileBreakpoint?: number;
  
  /** Show detail as modal on mobile */
  showDetailAsModal?: boolean;
  
  /** Header content (shown above split panels) */
  header?: ReactNode;
  
  /** Footer content (shown below split panels) */
  footer?: ReactNode;
  
  /** Master panel header */
  masterHeader?: ReactNode;
  
  /** Detail panel header */
  detailHeader?: ReactNode;
  
  /** Empty state for detail panel when nothing selected */
  detailEmptyState?: ReactNode;
  
  /** Additional className for container */
  className?: string;
  
  /** Additional className for master panel */
  masterClassName?: string;
  
  /** Additional className for detail panel */
  detailClassName?: string;
  
  /** Height of the shell (default: 100%) */
  height?: number | string;
}

// ============================================================================
// Default Empty State
// ============================================================================

function DefaultDetailEmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-[var(--text-ter)]">
      <div className="text-lg font-medium mb-2">No item selected</div>
      <div className="text-sm">Select an item from the list to view details</div>
    </div>
  );
}

// ============================================================================
// Mobile Modal Wrapper
// ============================================================================

interface MobileModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
}

function MobileModal({ isOpen, onClose, children }: MobileModalProps) {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal content */}
      <div className="absolute inset-x-0 bottom-0 top-12 bg-[var(--bg-raised)] shadow-xl overflow-hidden flex flex-col">
        {/* Close button */}
        <div className="flex items-center justify-end p-2 border-b border-[var(--rule)]">
          <button
            onClick={onClose}
            className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MasterDetailShell Component
// ============================================================================

export function MasterDetailShell({
  master,
  detail,
  selectedId: externalSelectedId,
  onSelectionChange,
  defaultMasterWidth = 400,
  minMasterWidth = 200,
  maxMasterWidth = 800,
  urlParamName: _urlParamName = 'selected',
  syncToUrl: _syncToUrl = true,
  widthStorageKey = 'master-detail-width',
  enableKeyboardShortcuts = true,
  mobileBreakpoint = 768,
  showDetailAsModal = true,
  header,
  footer,
  masterHeader,
  detailHeader,
  detailEmptyState,
  className = '',
  masterClassName = '',
  detailClassName = '',
  height = '100%',
}: MasterDetailShellProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Simple width state - no URL sync for selection to avoid double state
  const [masterWidth, setMasterWidth] = useState(() => {
    if (typeof window === 'undefined') return defaultMasterWidth;
    try {
      const stored = localStorage.getItem(widthStorageKey);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed > 0) {
          return Math.max(minMasterWidth, Math.min(maxMasterWidth, parsed));
        }
      }
    } catch { /* noop */ }
    return defaultMasterWidth;
  });
  const [_isResizing, setIsResizing] = useState(false);
  
  // Selection is fully controlled by parent - no internal state
  const selectedId = externalSelectedId ?? null;
  const isDetailOpen = selectedId !== null && detail !== null;
  
  // Simple close handler
  const handleClose = useCallback(() => {
    onSelectionChange?.(null);
  }, [onSelectionChange]);
  
  // Resize handlers
  const handleResizeStart = useCallback(() => setIsResizing(true), []);
  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    // Persist on end
    try {
      localStorage.setItem(widthStorageKey, String(Math.round(masterWidth)));
    } catch { /* noop */ }
  }, [widthStorageKey, masterWidth]);
  
  const handleResize = useCallback((deltaX: number) => {
    setMasterWidth(prev => Math.max(minMasterWidth, Math.min(maxMasterWidth, prev + deltaX)));
  }, [minMasterWidth, maxMasterWidth]);
  
  // Keyboard: Escape to close
  useEffect(() => {
    if (!enableKeyboardShortcuts || !isDetailOpen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enableKeyboardShortcuts, isDetailOpen, handleClose]);
  
  // Check if we're on mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < mobileBreakpoint;
  
  // Determine what to show in detail panel
  const showDetail = isDetailOpen;
  const detailContent = showDetail ? detail : (detailEmptyState || <DefaultDetailEmptyState />);
  
  // Mobile modal view
  if (isMobile && showDetailAsModal && showDetail) {
    return (
      <div className={`flex flex-col ${className}`} style={{ height }} ref={containerRef}>
        {/* Header */}
        {header && (
          <div className="flex-shrink-0">
            {header}
          </div>
        )}
        
        {/* Master panel only on mobile */}
        <div className={`flex-1 min-h-0 overflow-hidden flex flex-col ${masterClassName}`}>
          {masterHeader && (
            <div className="border-b border-[var(--rule)] bg-[var(--bg-raised)]">
              {masterHeader}
            </div>
          )}
          {master}
        </div>
        
        {/* Footer */}
        {footer && (
          <div className="flex-shrink-0">
            {footer}
          </div>
        )}
        
        {/* Detail as modal */}
        <MobileModal isOpen={isDetailOpen} onClose={handleClose}>
          {detailHeader && (
            <div className="border-b border-[var(--rule)] bg-[var(--bg-raised)] p-4">
              {detailHeader}
            </div>
          )}
          {detail}
        </MobileModal>
      </div>
    );
  }
  
  return (
    <div
      className={`flex flex-col bg-[var(--surface)] ${className}`}
      style={{ height }}
      ref={containerRef}
    >
      {/* Header */}
      {header && (
        <div className="flex-shrink-0 bg-[var(--bg-raised)] border-b border-[var(--rule)]">
          {header}
        </div>
      )}
      
      {/* Main split view */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Master panel */}
        <div
          className={`flex flex-col bg-[var(--bg-raised)] border-r border-[var(--rule)] overflow-hidden flex-shrink-0 ${masterClassName}`}
          style={{ width: showDetail ? masterWidth : '100%' }}
        >
          {masterHeader && (
            <div className="flex-shrink-0 border-b border-[var(--rule)] p-4">
              {masterHeader}
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            {master}
          </div>
        </div>
        
        {/* Resizable divider */}
        {showDetail && (
          <ResizableDivider
            onResizeStart={handleResizeStart}
            onResize={handleResize}
            onResizeEnd={handleResizeEnd}
          />
        )}
        
        {/* Detail panel */}
        {showDetail && (
          <div
            className={`flex flex-col flex-1 bg-[var(--bg-raised)] overflow-hidden min-w-0 ${detailClassName}`}
          >
            {/* Detail header with close button */}
            <div className="flex-shrink-0 flex items-center justify-between border-b border-[var(--rule)] px-4 py-2">
              <div className="flex-1 min-w-0">
                {detailHeader}
              </div>
              <button
                onClick={handleClose}
                className="ml-4 p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] flex-shrink-0"
                aria-label="Close detail"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Detail content */}
            <div className="flex-1 overflow-auto">
              {detailContent}
            </div>
          </div>
        )}
        
        {/* Empty state when no detail selected (desktop only, full width) */}
        {!showDetail && !isMobile && detailEmptyState && (
          <div className="hidden lg:flex flex-1 items-center justify-center bg-[var(--surface)] border-l border-[var(--rule)]">
            {detailEmptyState}
          </div>
        )}
      </div>
      
      {/* Footer */}
      {footer && (
        <div className="flex-shrink-0 bg-[var(--bg-raised)] border-t border-[var(--rule)]">
          {footer}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Controlled version hook
// ============================================================================

/**
 * Hook for using MasterDetailShell in fully controlled mode
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useMasterDetail(options?: MasterDetailStateOptions) {
  return useMasterDetailState(options);
}

export default MasterDetailShell;
